/**
 * Task #447 — Finance persona deep-dive page.
 *
 * Sections:
 *  1. Multi-year obligations ladder + cash-flow timing
 *  2. Exposure & risk (buyout today/+6/+12, ceiling, offset, watchlist, concentration)
 *  3. Forecast & pipeline (renewals + neg-in-flight + bonus accrual)
 *  4. Scenario modeling (termination + renewal, save/load)
 *  5. Budget vs actual (FY budget upload + variance)
 *  6. Action queue (comp/buyout approvals sorted by $ delta)
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DemoExportGate } from "@/components/DemoExportGate";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Download, AlertTriangle, TrendingUp, DollarSign, FileSpreadsheet, Calculator, Save, Trash2, ShieldAlert, Clock, ArrowLeftRight } from "lucide-react";
import { CountUp } from "@/components/motion";

interface LadderRow {
  fy: number;
  label: string;
  guaranteed: number;
  discretionary: number;
  total: number;
  bySport: Array<{ sport: string; guaranteed: number; discretionary: number; total: number }>;
}
interface CashFlowMonth {
  ym: string;
  label: string;
  base: number;
  bonuses: number;
  allowances: number;
  total: number;
}
interface ObligationsResp {
  fiscalYearStartMonth: number;
  currentFY: number;
  ladder: LadderRow[];
  cashflow: CashFlowMonth[];
}

interface ExposureResp {
  totals: { today: number; in6Months: number; in12Months: number; worstCaseCeiling: number; offsetAwareNet: number };
  perContract: Array<{
    contractId: number; coachName: string; sport: string; endDate: string | null;
    buyoutToday: number; buyoutIn6: number; buyoutIn12: number; offsetNet: number;
  }>;
  autoRenewWatchlist: Array<{
    contractId: number; coachName: string; sport: string; endDate: string | null;
    annualSalary: number; indicator: string;
  }>;
  counterpartyConcentration: Array<{
    id: number; name: string; firm: string | null; role: string;
    contractCount: number; totalCommitted: number;
  }>;
}

interface ForecastResp {
  inflationPct: number;
  renewalPipeline: Record<string, {
    windowMonths: number; contractCount: number; currentTotalAnnual: number;
    forecastTotalAnnual: number; delta: number;
    rows: Array<{ contractId: number; coachName: string; sport: string; endDate: string | null; currentAnnual: number; forecastAnnual: number; delta: number }>;
  }>;
  negotiationsInFlight: Array<{
    negotiationItemId: number; contractId: number; coachName: string; sport: string;
    summary: string; fieldRef: string | null; currentAnnual: number; inferredDelta: number;
  }>;
  bonusAccrual: {
    maxPossible: number;
    weightedForecast: number;
    earnedToDate: number;
    maxRemaining: number;
    incentives: Array<{
      contractId: number; coachName: string; sport: string;
      incentiveId: string; label: string; amount: number;
      condition: string | null; likelihood: "low" | "med" | "high"; weighted: number;
    }>;
  };
}

interface ScenarioRow {
  id: number; name: string; kind: "termination" | "renewal"; createdByEmail: string | null;
  notes: string | null; inputs: unknown; result: unknown; createdAt: string; updatedAt: string;
}

interface BudgetRow { id: number; fiscalYear: number; sport: string; budgetAmount: string; notes: string | null }
interface BudgetVsActualResp {
  fiscalYear: number; fiscalYearLabel: string;
  totals: { budget: number; actual: number; variance: number; utilizationPct: number | null };
  rows: Array<{ sport: string; budget: number; actual: number; variance: number; utilizationPct: number | null }>;
}

interface SnapshotRow { id: number; label: string; capturedAt: string; createdByUserId: string }

interface ActionQueueResp {
  pending: Array<{
    approvalId: number; contractId: number; coachName: string; sport: string;
    currentStage: string; isAmendment: boolean; submittedAt: string | null;
    annualSalary: number; buyoutClause: number; signingBonus: number; dollarImpact: number;
  }>;
  recentlyApproved: Array<{
    approvalId: number; contractId: number; coachName: string; sport: string;
    completedAt: string | null; annualSalary: number; signingBonus: number; impact: number;
  }>;
  cumulativeImpactByQuarter: Array<{ quarter: string; amount: number }>;
  cumulativeImpactTotal: number;
}

function fmtMoney(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json();
}

async function downloadXlsx(url: string, filename: string) {
  return downloadCsv(url, filename);
}

async function downloadCsv(url: string, filename: string) {
  const r = await fetch(url);
  if (!r.ok) throw new Error("Download failed");
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(u);
}

export default function FinancePage() {
  const { toast } = useToast();

  const [obligations, setObligations] = useState<ObligationsResp | null>(null);
  const [exposure, setExposure] = useState<ExposureResp | null>(null);
  const [inflation, setInflation] = useState(5);
  const [forecast, setForecast] = useState<ForecastResp | null>(null);
  const [scenarios, setScenarios] = useState<ScenarioRow[]>([]);
  const [budgets, setBudgets] = useState<BudgetRow[]>([]);
  const [bva, setBva] = useState<BudgetVsActualResp | null>(null);
  const [snapshots, setSnapshots] = useState<SnapshotRow[]>([]);
  const [actionQueue, setActionQueue] = useState<ActionQueueResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [cashflowGrain, setCashflowGrain] = useState<"month" | "quarter">("month");
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [compareData, setCompareData] = useState<{ scenarios: Array<{ id: number; name: string; kind: string; summary: Record<string, unknown> }> } | null>(null);
  const [editScenario, setEditScenario] = useState<ScenarioRow | null>(null);

  const reloadCore = () => {
    Promise.all([
      jsonFetch<ObligationsResp>("/api/finance/obligations"),
      jsonFetch<ExposureResp>("/api/finance/exposure"),
      jsonFetch<ScenarioRow[]>("/api/finance/scenarios"),
      jsonFetch<BudgetRow[]>("/api/finance/budgets"),
      jsonFetch<BudgetVsActualResp>("/api/finance/budget-vs-actual"),
      jsonFetch<SnapshotRow[]>("/api/finance/snapshots"),
      jsonFetch<ActionQueueResp>("/api/finance/action-queue"),
    ])
      .then(([ob, ex, sc, bg, bv, sn, aq]) => {
        setObligations(ob); setExposure(ex); setScenarios(sc);
        setBudgets(bg); setBva(bv); setSnapshots(sn); setActionQueue(aq);
      })
      .catch((err) => toast({ title: "Failed to load finance data", description: String(err), variant: "destructive" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reloadCore(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  useEffect(() => {
    jsonFetch<ForecastResp>(`/api/finance/forecast?inflationPct=${inflation}`)
      .then(setForecast)
      .catch(() => { /* keep prior */ });
  }, [inflation]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="page-finance">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Finance</h1>
          <p className="text-sm text-muted-foreground">
            Obligations, exposure, forecast, scenarios, budget vs. actual, and the action queue.
          </p>
        </div>
      </div>

      <FinanceSummaryCards
        obligations={obligations}
        exposure={exposure}
        forecast={forecast}
      />

      <Tabs defaultValue="obligations" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="obligations">Obligations</TabsTrigger>
          <TabsTrigger value="exposure">Exposure</TabsTrigger>
          <TabsTrigger value="forecast">Forecast</TabsTrigger>
          <TabsTrigger value="scenarios">Scenarios</TabsTrigger>
          <TabsTrigger value="budget">Budget vs. Actual</TabsTrigger>
          <TabsTrigger value="snapshots">Snapshots</TabsTrigger>
          <TabsTrigger value="queue">Action Queue</TabsTrigger>
        </TabsList>

        {/* ── Obligations ─────────────────────────── */}
        <TabsContent value="obligations" className="space-y-4">
          <div className="flex items-center justify-end">
            <DemoExportGate label="Export ladder">
              <Button size="sm" variant="outline" data-testid="btn-export-ladder-xlsx"
                onClick={() => downloadXlsx("/api/finance/exports/ladder.xlsx", "finance-ladder.xlsx")}>
                <Download className="h-3.5 w-3.5 mr-1" /> XLSX
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadCsv("/api/finance/exports/ladder.csv", "finance-ladder.csv")}>
                <Download className="h-3.5 w-3.5 mr-1.5" />Export ladder
              </Button>
            </DemoExportGate>
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base">Multi-year ladder</CardTitle>
              <CardDescription>FY rollups from {obligations?.currentFY} forward</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fiscal Year</TableHead>
                    <TableHead className="text-right">Guaranteed</TableHead>
                    <TableHead className="text-right">Discretionary</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {obligations?.ladder.map((row) => (
                    <TableRow key={row.fy} data-testid={`ladder-row-${row.fy}`}>
                      <TableCell className="font-medium">{row.label}</TableCell>
                      <TableCell className="text-right">{fmtMoney(row.guaranteed)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(row.discretionary)}</TableCell>
                      <TableCell className="text-right font-semibold">{fmtMoney(row.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base">Cash-flow timing (next 24 months)</CardTitle>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant={cashflowGrain === "month" ? "default" : "outline"}
                    data-testid="btn-cashflow-month" onClick={() => setCashflowGrain("month")}>
                    Month
                  </Button>
                  <Button size="sm" variant={cashflowGrain === "quarter" ? "default" : "outline"}
                    data-testid="btn-cashflow-quarter" onClick={() => setCashflowGrain("quarter")}>
                    Quarter
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{cashflowGrain === "month" ? "Month" : "Quarter"}</TableHead>
                      <TableHead className="text-right">Base</TableHead>
                      <TableHead className="text-right">Bonuses/One-time</TableHead>
                      <TableHead className="text-right">Allowances</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const months = obligations?.cashflow ?? [];
                      if (cashflowGrain === "month") {
                        return months.map((m) => (
                          <TableRow key={m.ym}>
                            <TableCell>{m.label}</TableCell>
                            <TableCell className="text-right">{fmtMoney(m.base)}</TableCell>
                            <TableCell className="text-right">{fmtMoney(m.bonuses)}</TableCell>
                            <TableCell className="text-right">{fmtMoney(m.allowances)}</TableCell>
                            <TableCell className="text-right font-semibold">{fmtMoney(m.total)}</TableCell>
                          </TableRow>
                        ));
                      }
                      // Quarter aggregation: bucket by calendar year + quarter (1-4).
                      const buckets = new Map<string, { base: number; bonuses: number; allowances: number; total: number }>();
                      for (const m of months) {
                        const [y, mm] = m.ym.split("-").map((s) => parseInt(s, 10));
                        const q = Math.floor(((mm || 1) - 1) / 3) + 1;
                        const key = `${y} Q${q}`;
                        const cur = buckets.get(key) ?? { base: 0, bonuses: 0, allowances: 0, total: 0 };
                        cur.base += m.base; cur.bonuses += m.bonuses; cur.allowances += m.allowances; cur.total += m.total;
                        buckets.set(key, cur);
                      }
                      return Array.from(buckets.entries()).map(([k, v]) => (
                        <TableRow key={k} data-testid={`cashflow-q-${k}`}>
                          <TableCell>{k}</TableCell>
                          <TableCell className="text-right">{fmtMoney(v.base)}</TableCell>
                          <TableCell className="text-right">{fmtMoney(v.bonuses)}</TableCell>
                          <TableCell className="text-right">{fmtMoney(v.allowances)}</TableCell>
                          <TableCell className="text-right font-semibold">{fmtMoney(v.total)}</TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Exposure ─────────────────────────── */}
        <TabsContent value="exposure" className="space-y-4">
          <div className="flex items-center justify-end">
            <DemoExportGate label="Export buyouts">
              <Button size="sm" variant="outline" data-testid="btn-export-buyouts-xlsx"
                onClick={() => downloadXlsx("/api/finance/exports/buyouts.xlsx", "finance-buyouts.xlsx")}>
                <Download className="h-3.5 w-3.5 mr-1" /> XLSX
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadCsv("/api/finance/exports/buyouts.csv", "finance-buyouts.csv")}>
                <Download className="h-3.5 w-3.5 mr-1.5" />Export buyouts
              </Button>
            </DemoExportGate>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Today", v: exposure?.totals.today, color: "text-orange-600" },
              { label: "+6 months", v: exposure?.totals.in6Months, color: "text-orange-500" },
              { label: "+12 months", v: exposure?.totals.in12Months, color: "text-amber-500" },
              { label: "Worst-case ceiling", v: exposure?.totals.worstCaseCeiling, color: "text-red-600" },
              { label: "Offset-aware net", v: exposure?.totals.offsetAwareNet, color: "text-green-700" },
            ].map((s) => (
              <Card key={s.label}>
                <CardContent className="py-3">
                  <div className="text-[11px] text-muted-foreground">{s.label}</div>
                  <div className={`text-xl font-bold mt-0.5 ${s.color}`}>{fmtMoney(s.v ?? 0)}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {exposure?.autoRenewWatchlist.length ? (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" />Auto-renewal / evergreen watchlist</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Coach</TableHead><TableHead>Sport</TableHead><TableHead>End date</TableHead><TableHead className="text-right">Annual</TableHead><TableHead>Indicator</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {exposure.autoRenewWatchlist.map((w) => (
                      <TableRow key={w.contractId}>
                        <TableCell>{w.coachName}</TableCell>
                        <TableCell>{w.sport}</TableCell>
                        <TableCell>{w.endDate ?? "—"}</TableCell>
                        <TableCell className="text-right">{fmtMoney(w.annualSalary)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{w.indicator}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader><CardTitle className="text-base">Counterparty / agency concentration</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Counterparty</TableHead><TableHead>Role</TableHead><TableHead className="text-right">Contracts</TableHead><TableHead className="text-right">Total committed</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {exposure?.counterpartyConcentration.map((cp) => (
                    <TableRow key={cp.id}>
                      <TableCell className="font-medium">{cp.name}{cp.firm ? <span className="text-muted-foreground"> · {cp.firm}</span> : null}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{cp.role}</Badge></TableCell>
                      <TableCell className="text-right">{cp.contractCount}</TableCell>
                      <TableCell className="text-right font-semibold">{fmtMoney(cp.totalCommitted)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Per-contract buyout exposure</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Coach</TableHead><TableHead>Sport</TableHead>
                    <TableHead className="text-right">Today</TableHead>
                    <TableHead className="text-right">+6mo</TableHead>
                    <TableHead className="text-right">+12mo</TableHead>
                    <TableHead className="text-right">Net (offset)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exposure?.perContract.slice(0, 50).map((r) => (
                    <TableRow key={r.contractId}>
                      <TableCell>{r.coachName}</TableCell>
                      <TableCell>{r.sport}</TableCell>
                      <TableCell className="text-right">{fmtMoney(r.buyoutToday)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(r.buyoutIn6)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(r.buyoutIn12)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(r.offsetNet)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Forecast ─────────────────────────── */}
        <TabsContent value="forecast" className="space-y-4">
          <div className="flex items-center gap-3 justify-between flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Inflation %</Label>
              <Input
                type="number" className="h-8 w-20"
                value={inflation}
                onChange={(e) => setInflation(parseFloat(e.target.value) || 0)}
                data-testid="input-inflation"
              />
            </div>
            <DemoExportGate label="Export forecast">
              <Button size="sm" variant="outline"
                onClick={() => downloadCsv(`/api/finance/exports/renewal-forecast.csv?inflationPct=${inflation}`, "renewal-forecast.csv")}>
                <Download className="h-3.5 w-3.5 mr-1" /> CSV
              </Button>
              <Button size="sm" variant="outline" data-testid="btn-export-forecast-xlsx"
                onClick={() => downloadXlsx(`/api/finance/exports/renewal-forecast.xlsx?inflationPct=${inflation}`, "renewal-forecast.xlsx")}>
                <Download className="h-3.5 w-3.5 mr-1.5" />Export forecast
              </Button>
            </DemoExportGate>
          </div>
          {forecast && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(["next12", "next24", "next36"] as const).map((key) => {
                  const w = forecast.renewalPipeline[key];
                  if (!w) return null;
                  return (
                    <Card key={key}>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Renewals next {w.windowMonths} mo</CardTitle></CardHeader>
                      <CardContent className="text-xs space-y-1">
                        <div className="flex justify-between"><span>Contracts</span><span className="font-semibold">{w.contractCount}</span></div>
                        <div className="flex justify-between"><span>Current annual</span><span>{fmtMoney(w.currentTotalAnnual)}</span></div>
                        <div className="flex justify-between"><span>Forecast annual</span><span className="font-semibold">{fmtMoney(w.forecastTotalAnnual)}</span></div>
                        <div className="flex justify-between"><span>Delta</span>
                          <span className={w.delta >= 0 ? "text-amber-600 font-semibold" : "text-green-700 font-semibold"}>{fmtMoney(w.delta)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Card>
                <CardHeader><CardTitle className="text-base">Negotiations in flight</CardTitle></CardHeader>
                <CardContent>
                  {forecast.negotiationsInFlight.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No open negotiations.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow><TableHead>Coach</TableHead><TableHead>Summary</TableHead><TableHead className="text-right">Inferred Δ</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {forecast.negotiationsInFlight.map((n) => (
                          <TableRow key={n.negotiationItemId}>
                            <TableCell>{n.coachName}</TableCell>
                            <TableCell className="text-xs max-w-md truncate">{n.summary}</TableCell>
                            <TableCell className="text-right">{n.inferredDelta ? fmtMoney(n.inferredDelta) : "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Bonus accrual outlook</CardTitle>
                  <CardDescription>
                    Max possible: <span className="font-semibold">{fmtMoney(forecast.bonusAccrual.maxPossible)}</span>{" · "}
                    Likelihood-weighted: <span className="font-semibold text-amber-700">{fmtMoney(forecast.bonusAccrual.weightedForecast)}</span>{" · "}
                    Earned to date: <span className="font-semibold text-emerald-700">{fmtMoney(forecast.bonusAccrual.earnedToDate)}</span>{" · "}
                    Max remaining: <span className="font-semibold">{fmtMoney(forecast.bonusAccrual.maxRemaining)}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Coach</TableHead><TableHead>Incentive</TableHead>
                        <TableHead className="text-right">Max</TableHead>
                        <TableHead>Likelihood</TableHead>
                        <TableHead className="text-right">Weighted</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {forecast.bonusAccrual.incentives.slice(0, 50).map((b) => (
                        <TableRow key={`${b.contractId}:${b.incentiveId}`}>
                          <TableCell>{b.coachName}</TableCell>
                          <TableCell className="text-xs">{b.label}</TableCell>
                          <TableCell className="text-right">{fmtMoney(b.amount)}</TableCell>
                          <TableCell>
                            <Select
                              value={b.likelihood}
                              onValueChange={async (v) => {
                                try {
                                  await jsonFetch(`/api/finance/incentives/${b.contractId}/${encodeURIComponent(b.incentiveId)}/likelihood`, {
                                    method: "PUT", body: JSON.stringify({ likelihood: v }),
                                  });
                                  jsonFetch<ForecastResp>(`/api/finance/forecast?inflationPct=${inflation}`).then(setForecast);
                                } catch (err) {
                                  toast({ title: "Failed to update", description: String(err), variant: "destructive" });
                                }
                              }}
                            >
                              <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">low</SelectItem>
                                <SelectItem value="med">med</SelectItem>
                                <SelectItem value="high">high</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right font-semibold">{fmtMoney(b.weighted)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ── Scenarios ─────────────────────────── */}
        <TabsContent value="scenarios" className="space-y-4">
          <ScenarioBuilder
            initial={editScenario}
            onCleared={() => setEditScenario(null)}
            onSaved={(s) => {
              setScenarios((prev) => {
                const without = prev.filter((x) => x.id !== s.id);
                return [s, ...without];
              });
              setEditScenario(null);
            }}
          />
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base">Saved scenarios</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={compareIds.length < 2}
                  data-testid="btn-compare-scenarios"
                  onClick={async () => {
                    try {
                      const data = await jsonFetch<{ scenarios: Array<{ id: number; name: string; kind: string; summary: Record<string, unknown> }> }>(
                        `/api/finance/scenarios/compare?ids=${compareIds.join(",")}`,
                      );
                      setCompareData(data);
                    } catch (err) {
                      toast({ title: "Compare failed", description: String(err), variant: "destructive" });
                    }
                  }}
                >
                  Compare ({compareIds.length})
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {scenarios.length === 0 ? (
                <p className="text-xs text-muted-foreground">No scenarios yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]"></TableHead>
                      <TableHead>Name</TableHead><TableHead>Kind</TableHead><TableHead>By</TableHead><TableHead>Updated</TableHead><TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scenarios.map((s) => (
                      <TableRow key={s.id} data-testid={`scenario-${s.id}`}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={compareIds.includes(s.id)}
                            disabled={!compareIds.includes(s.id) && compareIds.length >= 5}
                            onChange={(e) => {
                              setCompareIds((prev) => e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id));
                            }}
                            data-testid={`compare-check-${s.id}`}
                          />
                        </TableCell>
                        <TableCell>{s.name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{s.kind}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{s.createdByEmail ?? "—"}</TableCell>
                        <TableCell className="text-xs">{new Date(s.updatedAt).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="ghost" data-testid={`btn-load-${s.id}`}
                            onClick={() => setEditScenario(s)}>
                            Load
                          </Button>
                          <DemoExportGate label="Export" variant="ghost">
                            <Button size="sm" variant="ghost" title="CSV" onClick={() => downloadCsv(`/api/finance/exports/scenario-${s.id}.csv`, `scenario-${s.id}.csv`)}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" title="XLSX" data-testid={`btn-xlsx-${s.id}`}
                              onClick={() => downloadXlsx(`/api/finance/exports/scenario-${s.id}.xlsx`, `scenario-${s.id}.xlsx`)}>
                              <span className="text-[10px] font-semibold">XLSX</span>
                            </Button>
                          </DemoExportGate>
                          <Button size="sm" variant="ghost"
                            onClick={async () => {
                              if (!confirm("Delete scenario?")) return;
                              try {
                                await jsonFetch(`/api/finance/scenarios/${s.id}`, { method: "DELETE" });
                                setScenarios((prev) => prev.filter((x) => x.id !== s.id));
                                setCompareIds((prev) => prev.filter((x) => x !== s.id));
                              } catch (err) { toast({ title: "Delete failed", description: String(err), variant: "destructive" }); }
                            }}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          {compareData && (
            <Card data-testid="scenario-compare-panel">
              <CardHeader>
                <CardTitle className="text-base">Side-by-side compare</CardTitle>
                <CardDescription>{compareData.scenarios.length} scenarios</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Field</TableHead>
                      {compareData.scenarios.map((s) => (
                        <TableHead key={s.id} className="text-right">{s.name}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from(new Set(compareData.scenarios.flatMap((s) => Object.keys(s.summary)))).map((k) => (
                      <TableRow key={k}>
                        <TableCell className="font-medium text-xs">{k}</TableCell>
                        {compareData.scenarios.map((s) => {
                          const v = s.summary[k];
                          const display = typeof v === "number" ? fmtMoney(v) : (v == null ? "—" : String(v));
                          return <TableCell key={s.id} className="text-right text-xs">{display}</TableCell>;
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Budget vs Actual ─────────────────────────── */}
        <TabsContent value="budget" className="space-y-4">
          <BudgetEditor
            budgets={budgets}
            onChanged={() => Promise.all([
              jsonFetch<BudgetRow[]>("/api/finance/budgets").then(setBudgets),
              jsonFetch<BudgetVsActualResp>("/api/finance/budget-vs-actual").then(setBva),
            ])}
          />
          <div className="flex items-center justify-end">
            <DemoExportGate label="Export">
              <Button size="sm" variant="outline"
                onClick={() => downloadCsv(`/api/finance/exports/budget-vs-actual.csv?fiscalYear=${bva?.fiscalYear ?? ""}`, "budget-vs-actual.csv")}>
                <Download className="h-3.5 w-3.5 mr-1" /> CSV
              </Button>
              <Button size="sm" variant="outline" data-testid="btn-export-bva-xlsx"
                onClick={() => downloadXlsx(`/api/finance/exports/budget-vs-actual.xlsx?fiscalYear=${bva?.fiscalYear ?? ""}`, "budget-vs-actual.xlsx")}>
                <Download className="h-3.5 w-3.5 mr-1.5" />Export
              </Button>
            </DemoExportGate>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Variance — {bva?.fiscalYearLabel ?? ""}</CardTitle>
              <CardDescription>
                Budget {fmtMoney(bva?.totals.budget ?? 0)} · Actual {fmtMoney(bva?.totals.actual ?? 0)} · Variance{" "}
                <span className={(bva?.totals.variance ?? 0) >= 0 ? "text-green-700 font-semibold" : "text-signal-red font-semibold"}>
                  {fmtMoney(bva?.totals.variance ?? 0)}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Sport</TableHead><TableHead className="text-right">Budget</TableHead><TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Variance</TableHead><TableHead className="text-right">Util %</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {bva?.rows.map((r) => (
                    <TableRow key={r.sport} data-testid={`bva-row-${r.sport}`}>
                      <TableCell>{r.sport}</TableCell>
                      <TableCell className="text-right">{fmtMoney(r.budget)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(r.actual)}</TableCell>
                      <TableCell className={`text-right ${r.variance < 0 ? "text-signal-red" : ""}`}>{fmtMoney(r.variance)}</TableCell>
                      <TableCell className="text-right">{r.utilizationPct == null ? "—" : `${r.utilizationPct}%`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Snapshots ─────────────────────────── */}
        <TabsContent value="snapshots" className="space-y-4">
          <SnapshotPanel
            snapshots={snapshots}
            onChanged={() => jsonFetch<SnapshotRow[]>("/api/finance/snapshots").then(setSnapshots)}
          />
        </TabsContent>

        {/* ── Action Queue ─────────────────────────── */}
        <TabsContent value="queue" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pending comp/buyout approvals</CardTitle>
              <CardDescription>Sorted by dollar impact (annual + signing + buyout clause).</CardDescription>
            </CardHeader>
            <CardContent>
              {!actionQueue?.pending.length ? (
                <p className="text-xs text-muted-foreground">Nothing waiting on Finance right now.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Coach</TableHead><TableHead>Sport</TableHead><TableHead>Stage</TableHead>
                      <TableHead className="text-right">Annual</TableHead>
                      <TableHead className="text-right">Signing</TableHead>
                      <TableHead className="text-right">Buyout</TableHead>
                      <TableHead className="text-right">Δ Impact</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {actionQueue.pending.map((p) => (
                      <TableRow key={p.approvalId} data-testid={`pending-${p.approvalId}`}>
                        <TableCell>{p.coachName}</TableCell>
                        <TableCell>{p.sport}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{p.currentStage}</Badge></TableCell>
                        <TableCell className="text-right">{fmtMoney(p.annualSalary)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(p.signingBonus)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(p.buyoutClause)}</TableCell>
                        <TableCell className="text-right font-bold">{fmtMoney(p.dollarImpact)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recently approved (last 90 days)</CardTitle>
              <CardDescription>
                Cumulative budget impact: <span className="font-semibold">{fmtMoney(actionQueue?.cumulativeImpactTotal ?? 0)}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Quarter</TableHead><TableHead className="text-right">Impact</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {actionQueue?.cumulativeImpactByQuarter.map((q) => (
                    <TableRow key={q.quarter}><TableCell>{q.quarter}</TableCell><TableCell className="text-right">{fmtMoney(q.amount)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── FinanceSummaryCards ─────────────────────────────────────────

function FinanceKpiCard({
  title, value, sub, icon: Icon, color,
}: {
  title: string;
  value: number;
  sub?: string;
  icon: React.ElementType;
  color: "green" | "red" | "blue" | "gold";
}) {
  const valueColorMap = {
    green: "text-field-green",
    red: "text-signal-red",
    blue: "text-blue-600 dark:text-blue-400",
    gold: "text-gold",
  } as const;
  const iconColorMap = {
    green: "text-field-green",
    red: "text-signal-red",
    blue: "text-blue-500",
    gold: "text-gold",
  } as const;
  const iconBgMap = {
    green: "bg-field-green-subtle",
    red: "bg-signal-red-subtle",
    blue: "bg-blue-500/10",
    gold: "bg-gold-subtle",
  } as const;
  const gradientMap = {
    green: "kpi-card-green",
    red: "kpi-card-red",
    blue: "kpi-card-blue",
    gold: "kpi-card-gold",
  } as const;

  return (
    <Card className={`transition-all duration-200 animate-count-up card-hover-glow card-lift ${gradientMap[color]}`}>
      <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">
          {title}
        </CardTitle>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBgMap[color]}`}>
          <Icon className={`h-4 w-4 ${iconColorMap[color]}`} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className={`text-2xl font-bold kpi-value tracking-tight ${valueColorMap[color]}`}>
          <CountUp value={value} />
        </div>
        {sub ? <p className="text-xs text-muted-foreground mt-1 leading-tight">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

function FinanceSummaryCards({
  obligations, exposure, forecast,
}: {
  obligations: ObligationsResp | null;
  exposure: ExposureResp | null;
  forecast: ForecastResp | null;
}) {
  const totalGuaranteed = (obligations?.ladder ?? []).reduce((sum, r) => sum + (r.guaranteed || 0), 0);
  const totalBuyoutToday = exposure?.totals.today ?? 0;
  const expiring12 = forecast?.renewalPipeline?.next12?.contractCount ?? 0;
  const activeNegotiations = forecast?.negotiationsInFlight?.length ?? 0;

  // Money values are huge, so the CountUp animates the M/K-scaled
  // figure with a $ prefix and a unit suffix — same readability trick
  // the dashboard uses for headline financial KPIs.
  const moneyScale = (n: number): { v: number; suffix: string; decimals: number } => {
    if (Math.abs(n) >= 1_000_000) return { v: n / 1_000_000, suffix: "M", decimals: 2 };
    if (Math.abs(n) >= 1_000) return { v: n / 1_000, suffix: "K", decimals: 1 };
    return { v: Math.round(n), suffix: "", decimals: 0 };
  };
  const guaranteedScaled = moneyScale(totalGuaranteed);
  const buyoutScaled = moneyScale(totalBuyoutToday);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
      <Card className="transition-all duration-200 animate-count-up card-hover-glow card-lift kpi-card-gold">
        <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">
            Total guaranteed spend
          </CardTitle>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gold-subtle">
            <DollarSign className="h-4 w-4 text-gold" />
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="text-2xl font-bold kpi-value tracking-tight text-gold">
            <CountUp
              value={guaranteedScaled.v}
              prefix="$"
              suffix={guaranteedScaled.suffix}
              decimals={guaranteedScaled.decimals}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-tight">
            All guaranteed obligations across {obligations?.ladder.length ?? 0} fiscal years
          </p>
        </CardContent>
      </Card>

      <Card className="transition-all duration-200 animate-count-up card-hover-glow card-lift kpi-card-red">
        <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">
            Buyout exposure today
          </CardTitle>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-signal-red-subtle">
            <ShieldAlert className="h-4 w-4 text-signal-red" />
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="text-2xl font-bold kpi-value tracking-tight text-signal-red">
            <CountUp
              value={buyoutScaled.v}
              prefix="$"
              suffix={buyoutScaled.suffix}
              decimals={buyoutScaled.decimals}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-tight">
            If every contract terminated today, without offset
          </p>
        </CardContent>
      </Card>

      <FinanceKpiCard
        title="Expiring in 12 months"
        value={expiring12}
        sub="Contracts due for renewal"
        icon={Clock}
        color="blue"
      />

      <FinanceKpiCard
        title="Active negotiations"
        value={activeNegotiations}
        sub="Open items across all contracts"
        icon={ArrowLeftRight}
        color="green"
      />
    </div>
  );
}

// ── ScenarioBuilder ─────────────────────────────────────────────

function ScenarioBuilder({ onSaved, initial, onCleared }: {
  onSaved: (s: ScenarioRow) => void;
  initial?: ScenarioRow | null;
  onCleared?: () => void;
}) {
  const { toast } = useToast();
  const [kind, setKind] = useState<"termination" | "renewal">("termination");
  const [name, setName] = useState("");
  const [contractId, setContractId] = useState("");
  const [terminationDate, setTerminationDate] = useState(new Date().toISOString().slice(0, 10));
  const [terminationType, setTerminationType] = useState<"without_cause_university" | "with_cause" | "coach_initiated">("without_cause_university");
  const [windowMonths, setWindowMonths] = useState(12);
  const [inflationPct, setInflationPct] = useState(5);
  const [saving, setSaving] = useState(false);

  // Loading a saved scenario hydrates the inputs so Finance can tweak and save
  // a new revision (or save under a new name to keep both side-by-side).
  useEffect(() => {
    if (!initial) return;
    setKind(initial.kind);
    setName(`${initial.name} (revised)`);
    const inp = (initial.inputs ?? {}) as Record<string, unknown>;
    if (initial.kind === "termination") {
      setContractId(String(inp.contractId ?? ""));
      setTerminationDate(String(inp.terminationDate ?? new Date().toISOString().slice(0, 10)));
      setTerminationType((inp.terminationType as "without_cause_university" | "with_cause" | "coach_initiated") ?? "without_cause_university");
    } else {
      setWindowMonths(Number(inp.windowMonths ?? 12));
      setInflationPct(Number(inp.inflationPct ?? 5));
    }
  }, [initial]);

  async function save() {
    setSaving(true);
    try {
      const body = kind === "termination"
        ? {
            kind, name, inputs: {
              contractId: parseInt(contractId), terminationDate, terminationType,
            },
          }
        : { kind, name, inputs: { windowMonths, inflationPct } };
      const row = await jsonFetch<ScenarioRow>("/api/finance/scenarios", {
        method: "POST", body: JSON.stringify(body),
      });
      onSaved(row);
      toast({ title: "Scenario saved" });
      setName("");
    } catch (err) {
      toast({ title: "Save failed", description: String(err), variant: "destructive" });
    } finally { setSaving(false); }
  }

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calculator className="h-4 w-4" />Build a scenario</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as "termination" | "renewal")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="termination">Termination</SelectItem>
                <SelectItem value="renewal">Renewal pipeline</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Coach Smith Jan 2027 termination" data-testid="input-scenario-name" />
          </div>
        </div>
        {kind === "termination" ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Contract ID</Label>
              <Input type="number" value={contractId} onChange={(e) => setContractId(e.target.value)} data-testid="input-contract-id" />
            </div>
            <div>
              <Label className="text-xs">Termination date</Label>
              <Input type="date" value={terminationDate} onChange={(e) => setTerminationDate(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={terminationType} onValueChange={(v) => setTerminationType(v as "without_cause_university" | "with_cause" | "coach_initiated")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="without_cause_university">Without cause (univ.)</SelectItem>
                  <SelectItem value="with_cause">With cause</SelectItem>
                  <SelectItem value="coach_initiated">Coach-initiated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Window (months)</Label>
              <Input type="number" value={windowMonths} onChange={(e) => setWindowMonths(parseInt(e.target.value) || 12)} />
            </div>
            <div>
              <Label className="text-xs">Inflation %</Label>
              <Input type="number" value={inflationPct} onChange={(e) => setInflationPct(parseFloat(e.target.value) || 0)} />
            </div>
          </div>
        )}
        <Button onClick={save} disabled={saving || !name} data-testid="btn-save-scenario">
          <Save className="h-3.5 w-3.5 mr-1.5" />Save & evaluate
        </Button>
      </CardContent>
    </Card>
  );
}

// ── BudgetEditor ─────────────────────────────────────────────

function BudgetEditor({ budgets, onChanged }: { budgets: BudgetRow[]; onChanged: () => void }) {
  const { toast } = useToast();
  const [fy, setFy] = useState(new Date().getFullYear());
  const [sport, setSport] = useState("");
  const [amt, setAmt] = useState(0);
  const [csv, setCsv] = useState("sport,budget_amount\nFootball,1000000\nBasketball,500000");

  async function add() {
    try {
      await jsonFetch("/api/finance/budgets", { method: "PUT", body: JSON.stringify({ fiscalYear: fy, sport, budgetAmount: amt }) });
      onChanged(); setSport(""); setAmt(0);
    } catch (err) {
      toast({ title: "Save failed", description: String(err), variant: "destructive" });
    }
  }
  async function uploadCsv() {
    try {
      const r = await jsonFetch<{ inserted: number; errors: Array<{ line: number; reason: string }> }>(
        "/api/finance/budgets/import-csv",
        { method: "POST", body: JSON.stringify({ fiscalYear: fy, csv }) },
      );
      toast({ title: `Imported ${r.inserted} rows`, description: r.errors.length ? `${r.errors.length} errors` : undefined });
      onChanged();
    } catch (err) {
      toast({ title: "Import failed", description: String(err), variant: "destructive" });
    }
  }

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" />FY budget</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">FY</Label>
            <Input type="number" value={fy} onChange={(e) => setFy(parseInt(e.target.value) || fy)} />
          </div>
          <div>
            <Label className="text-xs">Sport</Label>
            <Input value={sport} onChange={(e) => setSport(e.target.value)} data-testid="input-budget-sport" />
          </div>
          <div>
            <Label className="text-xs">Budget $</Label>
            <Input type="number" value={amt} onChange={(e) => setAmt(parseFloat(e.target.value) || 0)} />
          </div>
          <Button onClick={add} disabled={!sport} data-testid="btn-save-budget">Save</Button>
        </div>
        <div>
          <Label className="text-xs">Bulk upload CSV (sport,budget_amount[,notes])</Label>
          <Textarea rows={4} value={csv} onChange={(e) => setCsv(e.target.value)} className="font-mono text-xs" />
          <Button onClick={uploadCsv} className="mt-2" size="sm" variant="outline">Import CSV</Button>
        </div>
        <div>
          <Label className="text-xs mb-1 block">Saved budget lines</Label>
          {budgets.length === 0 ? <p className="text-xs text-muted-foreground">None yet.</p> : (
            <Table>
              <TableHeader>
                <TableRow><TableHead>FY</TableHead><TableHead>Sport</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {budgets.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>{b.fiscalYear}</TableCell>
                    <TableCell>{b.sport}</TableCell>
                    <TableCell className="text-right">{fmtMoney(parseFloat(b.budgetAmount))}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost"
                        onClick={async () => {
                          try { await jsonFetch(`/api/finance/budgets/${b.id}`, { method: "DELETE" }); onChanged(); }
                          catch (err) { toast({ title: "Delete failed", description: String(err), variant: "destructive" }); }
                        }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── SnapshotPanel ─────────────────────────────────────────────

function SnapshotPanel({ snapshots, onChanged }: { snapshots: SnapshotRow[]; onChanged: () => void }) {
  const { toast } = useToast();
  const [label, setLabel] = useState("");
  const [diff, setDiff] = useState<unknown>(null);

  return (
    <>
      <Card>
        <CardHeader><CardTitle className="text-base">Capture snapshot</CardTitle>
          <CardDescription>Records today's committed bill so it can be diffed later.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs">Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Q1 2026 baseline" data-testid="input-snapshot-label" />
          </div>
          <Button
            onClick={async () => {
              if (!label) return;
              try {
                await jsonFetch("/api/finance/snapshots", { method: "POST", body: JSON.stringify({ label }) });
                onChanged(); setLabel("");
                toast({ title: "Snapshot captured" });
              } catch (err) { toast({ title: "Capture failed", description: String(err), variant: "destructive" }); }
            }}
            data-testid="btn-snapshot"
          ><Save className="h-3.5 w-3.5 mr-1.5" />Capture</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Saved snapshots</CardTitle></CardHeader>
        <CardContent>
          {snapshots.length === 0 ? (
            <p className="text-xs text-muted-foreground">None yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow><TableHead>Label</TableHead><TableHead>Captured</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {snapshots.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.label}</TableCell>
                    <TableCell className="text-xs">{new Date(s.capturedAt).toLocaleString()}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="outline"
                        onClick={async () => {
                          try { setDiff(await jsonFetch(`/api/finance/snapshots/${s.id}/diff`)); }
                          catch (err) { toast({ title: "Diff failed", description: String(err), variant: "destructive" }); }
                        }}>Diff</Button>
                      <Button size="sm" variant="ghost"
                        onClick={async () => {
                          if (!confirm("Delete snapshot?")) return;
                          try { await jsonFetch(`/api/finance/snapshots/${s.id}`, { method: "DELETE" }); onChanged(); }
                          catch (err) { toast({ title: "Delete failed", description: String(err), variant: "destructive" }); }
                        }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {diff != null && (
        <Card>
          <CardHeader><CardTitle className="text-base">Diff vs today</CardTitle></CardHeader>
          <CardContent>
            <pre className="text-xs overflow-x-auto bg-muted p-3 rounded-md max-h-96">{JSON.stringify(diff, null, 2)}</pre>
          </CardContent>
        </Card>
      )}
    </>
  );
}
